import { useDocEngine, useGetDocumentWithAllVersions } from "@hooks";
import { Typography } from "@mui/material";
import {
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { ErrorBoundary } from "src/components/ErrorBoundary";
import type {
  DocumentEditAnnotationEffects,
  TiptapDocumentEditOperation,
} from "src/tiptap/lib/document-edit-preview";
import { isHumanInTheLoopRejection } from "../../human-in-the-loop";
import { useToolApproval } from "../../ToolApproval";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import { EditToolCallFrame } from "./EditToolCallFrame";
import { ToolCallFrame } from "./ToolCallFrame";
import { useTiptapDocumentEditPreview } from "./useTiptapDocumentEditPreview";

const DocumentEditComparison = lazy(() => import("./DocumentEditComparison"));

const describeAnnotationEffects = (
  effects: DocumentEditAnnotationEffects | undefined
): string => {
  if (!effects) return "";
  const item = (count: number, name: string) =>
    `${count} ${name}${count === 1 ? "" : "s"}`;
  const removed = [
    effects.removedSuggestionCount
      ? item(effects.removedSuggestionCount, "tracked suggestion")
      : null,
    effects.removedCommentAnchorCount
      ? item(effects.removedCommentAnchorCount, "comment anchor")
      : null,
  ].filter(Boolean);
  const added = [
    effects.addedSuggestionCount
      ? item(effects.addedSuggestionCount, "tracked suggestion")
      : null,
    effects.addedCommentAnchorCount
      ? item(effects.addedCommentAnchorCount, "comment anchor")
      : null,
  ].filter(Boolean);
  const changes = [
    removed.length ? `removes ${removed.join(" and ")}` : null,
    added.length ? `adds ${added.join(" and ")}` : null,
  ].filter(Boolean);
  return changes.length ? `This edit ${changes.join(" and ")}.` : "";
};

export interface TiptapEditDocumentToolProps {
  args: Record<string, any>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
  approvalControls?: (options: {
    approveDisabled: boolean;
    beforeApprove: () => Promise<boolean>;
  }) => ReactNode;
}

function getDocumentIdFromPath(documentId: string): string {
  const segments = documentId.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? documentId;
}

function parseToolResult(content: unknown): Record<string, any> | undefined {
  if (!content) return undefined;
  if (typeof content === "object") return content as Record<string, any>;
  if (typeof content !== "string") return undefined;

  try {
    const parsed = JSON.parse(content);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, any>)
      : undefined;
  } catch {
    return undefined;
  }
}

function getStringValue(value: unknown): string {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : "";
}

function getEditOperations(value: unknown): TiptapDocumentEditOperation[] {
  if (!Array.isArray(value)) return [];
  if (
    !value.every(
      (operation) =>
        operation &&
        ["replace", "insertBefore", "insertAfter"].includes(operation.type) &&
        typeof operation.target === "string" &&
        Array.isArray(operation.content) &&
        operation.content.every(
          (node: unknown) =>
            node &&
            typeof node === "object" &&
            typeof (node as { type?: unknown }).type === "string"
        )
    )
  ) {
    return [];
  }
  return value.map(({ type, target, content }) => ({ type, target, content }));
}

function findFirstStringInArray(value: unknown): string {
  if (!Array.isArray(value)) {
    return "";
  }

  for (const item of value) {
    const text = getStringValue(item);
    if (text) {
      return text;
    }
  }

  return "";
}

function getToolSuggestionId(
  parsedContent: Record<string, any> | undefined
): string {
  return findFirstStringInArray(parsedContent?.suggestionIds);
}

function getErrorMessage(
  toolResult: ChatToolMessage | undefined,
  parsedContent: Record<string, any> | undefined
): string {
  if (toolResult?.status !== "error") {
    return "";
  }

  const parsedError =
    getStringValue(parsedContent?.error) ||
    getStringValue(parsedContent?.message);

  if (parsedError) {
    return parsedError;
  }

  const rawContent = getStringValue(toolResult.content);
  return rawContent || "The tracked edit could not be applied.";
}

const TiptapEditDocumentTool = ({
  args,
  toolResult,
  isLoading = false,
  approvalControls,
}: TiptapEditDocumentToolProps) => {
  const { orgId } = useChatDeviceContext();
  const suggestionNavigationCounterRef = useRef(0);
  const [reviewMessage, setReviewMessage] = useState("");
  const [comparisonRenderFailed, setComparisonRenderFailed] = useState(false);
  const approval = useToolApproval();

  const parsedContent = useMemo(
    () => parseToolResult(toolResult?.content),
    [toolResult?.content]
  );
  const rawArgsDocumentId =
    getStringValue(args.document_id) || getStringValue(args.id);
  const resultDocumentId =
    getStringValue(parsedContent?.documentId) ||
    getStringValue(parsedContent?.tiptapDocumentId);
  const documentId = rawArgsDocumentId || resultDocumentId;
  const documentIdFromPath = documentId
    ? getDocumentIdFromPath(documentId)
    : "";
  const { data: document, isError: documentUnavailable } =
    useGetDocumentWithAllVersions(orgId, documentIdFromPath, {
      throwOnError: false,
    });
  const { navigateToQMSDocument } = useDocEngine(document?.deviceId ?? "all");
  const navigationDocumentId = document?.id ?? documentIdFromPath;
  const documentTitle = document?.title || documentIdFromPath || "document";
  const operations = useMemo(
    () => getEditOperations(args.operations),
    [args.operations]
  );
  const isPendingReview = Boolean(approvalControls) && !toolResult;
  const preview = useTiptapDocumentEditPreview({
    orgId,
    documentName: document?.tiptapPath || documentId,
    operations,
    enabled: isPendingReview,
  });
  const comparison = preview.data?.comparison;
  const annotationSummary = describeAnnotationEffects(
    preview.data?.annotationEffects
  );
  const canApprove =
    !!preview.data?.source &&
    !preview.isError &&
    !preview.isFetching &&
    !comparisonRenderFailed &&
    (Boolean(comparison?.hasChanges) || Boolean(annotationSummary));
  const beforeApprove = useCallback(async () => {
    const shownSource = preview.data?.source;
    if (!shownSource) return false;

    const current = await preview.refetch();
    if (current.isError || !current.data) {
      setReviewMessage("The document could not be checked. Try again.");
      return false;
    }
    if (JSON.stringify(shownSource) !== JSON.stringify(current.data.source)) {
      setReviewMessage(
        "The document changed. Review the updated preview before approval."
      );
      return false;
    }
    setReviewMessage("");
    return true;
  }, [preview.data?.source, preview.refetch]);
  const versionId =
    getStringValue(parsedContent?.versionId) ||
    getStringValue(parsedContent?.version_id);
  const suggestionId = useMemo(
    () => getToolSuggestionId(parsedContent),
    [parsedContent]
  );
  const isRejected = isHumanInTheLoopRejection(toolResult);
  const hasError = toolResult?.status === "error" && !isRejected;
  const errorMessage = isRejected
    ? ""
    : getErrorMessage(toolResult, parsedContent);

  const handleOpenDocument = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!navigationDocumentId) {
        return;
      }

      const searchParams: Record<string, string> = {};

      if (suggestionId) {
        searchParams.tiptapSuggestionId = suggestionId;
      }

      const hasScrollTarget = Object.keys(searchParams).length > 0;

      if (hasScrollTarget) {
        suggestionNavigationCounterRef.current += 1;
      }

      navigateToQMSDocument({
        documentId: navigationDocumentId,
        searchParams: hasScrollTarget ? searchParams : undefined,
        state: hasScrollTarget
          ? {
              tiptapSuggestionNavigationKey:
                suggestionNavigationCounterRef.current,
            }
          : undefined,
        versionId: versionId || undefined,
      });
    },
    [navigateToQMSDocument, navigationDocumentId, suggestionId, versionId]
  );

  const proposedChanges = isPendingReview ? (
    <section aria-label="Proposed changes">
      {operations.length === 0 || preview.isError ? (
        <p className="m-0 text-xs text-gray-600">Preview unavailable.</p>
      ) : preview.isPending || preview.isFetching ? (
        <p className="m-0 text-xs text-gray-600">Loading change preview…</p>
      ) : comparison?.hasChanges ? (
        <ErrorBoundary
          componentName="Document edit comparison"
          onError={() => setComparisonRenderFailed(true)}
          fallback={
            <p className="m-0 text-xs text-gray-600">Preview unavailable.</p>
          }
        >
          <Suspense fallback={<p>Loading change preview…</p>}>
            <DocumentEditComparison
              comparison={comparison}
              excerptOnly={isPendingReview}
            />
          </Suspense>
        </ErrorBoundary>
      ) : annotationSummary ? (
        <p className="m-0 text-xs text-gray-600">
          Text and formatting unchanged.
        </p>
      ) : (
        <p className="m-0 text-xs text-gray-600">
          No visible content change in the current document.
        </p>
      )}
      {annotationSummary && (
        <p className="mt-2 mb-0 text-xs text-gray-700">{annotationSummary}</p>
      )}
      {reviewMessage && (
        <p className="mt-2 mb-0 text-xs text-amber-800" role="alert">
          {reviewMessage}
        </p>
      )}
    </section>
  ) : null;

  if (isPendingReview && approval) {
    return (
      <EditToolCallFrame
        toolName="edit_document"
        label={`Edit document ${documentTitle}`}
        approvalTitle={`Edit Document ${documentTitle}`}
        title={
          <>
            Edit document{" "}
            <button
              type="button"
              aria-label={`Open ${documentTitle}`}
              className="cursor-pointer border-0 bg-transparent p-0 text-[13px] text-[#074ee8] hover:underline"
              onClick={handleOpenDocument}
            >
              {documentTitle}
            </button>
          </>
        }
        controls={approvalControls?.({
          approveDisabled: !canApprove,
          beforeApprove,
        })}
        footer={
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-xs text-[#074ee8] hover:underline"
            onClick={handleOpenDocument}
            disabled={!navigationDocumentId}
          >
            Show full document
          </button>
        }
      >
        {proposedChanges}
      </EditToolCallFrame>
    );
  }

  return (
    <ToolCallFrame
      title={
        <span className="flex min-w-0 items-baseline gap-1 text-sm leading-5">
          <span className="shrink-0 text-sm leading-5">Edit Document</span>
          <button
            aria-label={`Open ${documentTitle}`}
            title={documentTitle}
            className="m-0 min-w-0 truncate text-left cursor-pointer rounded-sm border-0 bg-transparent p-0 text-sm leading-5 text-blue-600 no-underline underline-offset-2 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-default disabled:text-gray-500 disabled:no-underline"
            disabled={!document || documentUnavailable}
            onClick={handleOpenDocument}
            type="button"
          >
            {documentTitle}
          </button>
        </span>
      }
      isLoading={isLoading}
      hasError={hasError}
      isRejected={isRejected}
      isExpandable={isPendingReview}
      expandedByDefault={isPendingReview}
      titleContainsInteractiveContent={isPendingReview}
      hasToolResult={Boolean(toolResult)}
      hasContent={hasError || isPendingReview}
      footer={approvalControls?.({
        approveDisabled: !canApprove,
        beforeApprove,
      })}
    >
      {isPendingReview ? (
        proposedChanges
      ) : errorMessage ? (
        <Typography className="text-sm text-red-600" component="span">
          {errorMessage}
        </Typography>
      ) : null}
    </ToolCallFrame>
  );
};

export default TiptapEditDocumentTool;

TiptapEditDocumentTool.displayName = "TiptapEditDocumentTool";
